import { Stack, Typography } from "@mui/material";
import { AlertCircle } from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { CheckIcon } from "lucide-react";
import { ValidateCSVAndUnmapSPoCsErrors } from "../../store/SPoCDownloadAndUnmapStore";
import { ICON_SIZE, getIconProps } from "@pulse/shared-components";
import {
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import { SPoCDownloadAndUnmapStore } from "../../store/SPoCDownloadAndUnmapStore";

interface UnmapMultipleSPoCsValidationResultProps {
  t: TFunction;
  spacing: Spacing;
  tokens: FoundationColorTokens<string>;
  typography: LeoTypography;
  spocDownloadAndUnmapStore: Instance<typeof SPoCDownloadAndUnmapStore> | null;
}

export const UnmapMultipleSPoCsValidationResult = observer(
  ({
    spocDownloadAndUnmapStore,
    spacing,
    t,
    tokens,
    typography,
  }: UnmapMultipleSPoCsValidationResultProps): React.ReactElement => {
    let validationResultDialogDescription: string;
    let validationResultDialogDescriptionColor: string;
    let validationResultDialogIcon: JSX.Element;

    if (spocDownloadAndUnmapStore?.rpcErrors) {
      validationResultDialogIcon = (
        <AlertCircle {...getIconProps(tokens.iconError, ICON_SIZE.large)} />
      );
      validationResultDialogDescriptionColor = tokens.labelError;
      switch (spocDownloadAndUnmapStore.rpcErrors) {
        case ValidateCSVAndUnmapSPoCsErrors.InvalidFile: {
          validationResultDialogDescription = t(
            "projects.userAccess.unmapSPoCs.invalidFileErrorDescription",
            {
              defaultValue: t(
                "common.fileUploadErrorMessage.invalidFileFormat",
              ),
            },
          );
          break;
        }
        case ValidateCSVAndUnmapSPoCsErrors.ProjectArchived: {
          validationResultDialogDescription = t(
            "projects.userAccess.unmapSPoCs.projectArchivedDescription",
            {
              defaultValue: t("common.projectAlreadyArchivedErrorText"),
            },
          );
          break;
        }
        default: {
          validationResultDialogDescription = t(
            "projects.userAccess.unmapSPoCs.unexpectedErrorDescription",
            {
              defaultValue: t(
                "common.fileUploadErrorMessage.uploadErrorMessageText",
              ),
            },
          );
          break;
        }
      }
    } else {
      validationResultDialogDescription = t(
        "projects.userAccess.unmapSPoCs.successDescription",
        {
          defaultValue: t(
            "projects.userAccess.addMultipleSPoCsDialog.successText",
          ),
        },
      );
      validationResultDialogDescriptionColor = tokens.labelSuccess;
      validationResultDialogIcon = (
        <CheckIcon {...getIconProps(tokens.iconSuccess, ICON_SIZE.large)} />
      );
    }

    return (
      <Stack
        alignItems="center"
        spacing={spacing.spaceXS}
        padding={spacing.spaceXL}
        width="100%"
        textAlign="center"
      >
        {validationResultDialogIcon}
        <Typography
          sx={{
            ...typography.b1,
            color: validationResultDialogDescriptionColor,
          }}
        >
          {validationResultDialogDescription}
        </Typography>
      </Stack>
    );
  },
);
